import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiClock, FiCrop, FiFilm, FiMaximize, FiSliders, FiX } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import type { CanvasFitMode, CanvasPreset, ConversionSettings, TargetMimeType, VideoResolution } from '../types';

interface SliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  onChange: (value: number) => void;
  hint?: string;
}

const Slider = ({ label, value, min, max, step, unit, onChange, hint }: SliderProps) => (
  <div className="space-y-2">
    <div className="flex justify-between items-center">
      <label htmlFor={`setting-${label.toLowerCase().replace(/\s+/g, '-')}`} className="text-sm font-bold text-slate-700 dark:text-slate-200">{label}</label>
      <span className="text-sm font-black text-teal-600 dark:text-teal-400 tabular-nums min-w-[48px] text-right">
        {value}{unit}
      </span>
    </div>
    <div className="relative h-5 flex items-center">
      <div className="absolute inset-x-0 h-1.5 rounded-full bg-slate-200 dark:bg-slate-700" />
      <div
        className="absolute h-1.5 rounded-full bg-gradient-to-r from-teal-500 to-lime-500"
        style={{ width: `${((value - min) / (max - min)) * 100}%` }}
      />
      <input
        id={`setting-${label.toLowerCase().replace(/\s+/g, '-')}`}
        name={`setting-${label.toLowerCase().replace(/\s+/g, '-')}`}
        type="range"
        min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="relative w-full h-5 appearance-none bg-transparent cursor-pointer z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 rounded-full [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-teal-500 [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:hover:scale-110"
      />
    </div>
    {hint && <p className="text-[11px] text-slate-400 leading-tight">{hint}</p>}
  </div>
);

interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  triggerRef: RefObject<HTMLButtonElement | null>;
  targetFormat: TargetMimeType;
  settings: ConversionSettings;
  setters: {
    setCompression: (value: number) => void;
    setResolution: (value: VideoResolution) => void;
    setGifFps: (value: number) => void;
    setGifSpeed: (value: number) => void;
    setCanvasPreset: (value: CanvasPreset) => void;
    setFitMode: (value: CanvasFitMode) => void;
  };
}

const RESOLUTION_OPTIONS: Array<{ value: VideoResolution; label: string }> = [
  { value: 'original', label: 'Original' },
  { value: '1080p', label: '1080p' },
  { value: '720p', label: '720p' },
  { value: '480p', label: '480p' },
];

const CANVAS_OPTIONS: Array<{ value: CanvasPreset; label: string }> = [
  { value: 'original', label: 'Original' },
  { value: 'instagram-reel', label: 'Reel / TikTok / Shorts 9:16' },
  { value: 'youtube', label: 'YouTube 16:9' },
  { value: 'square', label: 'Square 1:1' },
  { value: 'portrait-feed', label: 'Portrait Feed 4:5' },
];

const FIT_OPTIONS: Array<{ value: CanvasFitMode; label: string }> = [
  { value: 'blur', label: 'Blur background' },
  { value: 'crop', label: 'Crop to fill' },
  { value: 'pad', label: 'Fit with bars' },
];

const SettingsPanel = ({
  isOpen,
  onClose,
  triggerRef,
  targetFormat,
  settings,
  setters,
}: SettingsPanelProps) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const isGif = targetFormat === 'image/gif';
  const { compression, resolution, gifFps, gifSpeed, canvasPreset, fitMode } = settings;
  const { setCompression, setResolution, setGifFps, setGifSpeed, setCanvasPreset, setFitMode } = setters;

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (e: MouseEvent) => {
      if (!(e.target instanceof Node)) return;
      const insidePanel = panelRef.current?.contains(e.target);
      const insideTrigger = triggerRef.current?.contains(e.target);
      if (!insidePanel && !insideTrigger) onClose();
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, triggerRef]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60]"
            onClick={onClose}
          />
          <motion.div
            ref={panelRef}
            initial={{ opacity: 0, scale: 0.95, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -8 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            className="absolute right-0 top-full mt-2 z-[90] w-[min(20rem,calc(100vw-2rem))] bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-700 p-6 space-y-6"
          >
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="bg-teal-500 p-2 rounded-xl text-white">
                  <SafeIcon icon={FiSliders} className="text-sm" />
                </div>
                <div>
                  <h3 className="font-black text-slate-800 dark:text-white text-sm leading-none">Settings</h3>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mt-1">Adjust Output</p>
                </div>
              </div>
              <button
                onClick={onClose}
                aria-label="Close conversion settings"
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                type="button"
              >
                <SafeIcon icon={FiX} className="text-sm" />
              </button>
            </div>

            <div className="h-px bg-slate-100 dark:bg-slate-800" />

            <div className="space-y-6">
              <div className="space-y-1">
                <div className="flex items-center gap-2 mb-3">
                  <SafeIcon icon={FiFilm} className="text-teal-500 text-xs" />
                  <span className="text-[11px] font-black uppercase tracking-widest text-slate-400">Encoding</span>
                </div>
                <Slider
                  label="Compression" value={compression}
                  min={10} max={100} step={5} unit="%"
                  onChange={setCompression}
                  hint={compression >= 85 ? 'Higher quality, larger videos' : compression >= 60 ? 'Balanced quality and size' : 'Smaller files, more compression'}
                />
              </div>

              <div className="space-y-1">
                <div className="flex items-center gap-2 mb-3">
                  <SafeIcon icon={FiMaximize} className="text-lime-500 text-xs" />
                  <span className="text-[11px] font-black uppercase tracking-widest text-slate-400">Dimensions</span>
                </div>
                <div className="space-y-2">
                  <label htmlFor="setting-resolution" className="text-sm font-bold text-slate-700 dark:text-slate-200">
                    Resolution
                  </label>
                  <select
                    id="setting-resolution"
                    value={resolution}
                    onChange={(e) => setResolution(e.target.value as VideoResolution)}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none focus-visible:ring-2 focus-visible:ring-teal-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  >
                    {RESOLUTION_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-slate-400 leading-tight">
                    {resolution === 'original' ? 'Keep the source dimensions' : `Scale output height to ${resolution}`}
                  </p>
                </div>
              </div>

              {isGif ? (
                <div className="space-y-5">
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiClock} className="text-teal-500 text-xs" />
                    <span className="text-[11px] font-black uppercase tracking-widest text-slate-400">GIF Timing</span>
                  </div>
                  <Slider
                    label="Frames"
                    value={gifFps}
                    min={4}
                    max={30}
                    step={1}
                    unit=" fps"
                    onChange={setGifFps}
                    hint={`${gifFps} output frame${gifFps === 1 ? '' : 's'} per second`}
                  />
                  <Slider
                    label="Speed"
                    value={gifSpeed}
                    min={0.25}
                    max={4}
                    step={0.25}
                    unit="x"
                    onChange={setGifSpeed}
                    hint={gifSpeed === 1 ? 'Keep original timing' : gifSpeed > 1 ? `Play ${gifSpeed}x faster` : `Play ${gifSpeed}x slower`}
                  />
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiCrop} className="text-teal-500 text-xs" />
                    <span className="text-[11px] font-black uppercase tracking-widest text-slate-400">Framing</span>
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="setting-canvas" className="text-sm font-bold text-slate-700 dark:text-slate-200">
                      Canvas
                    </label>
                    <select
                      id="setting-canvas"
                      value={canvasPreset}
                      onChange={(e) => setCanvasPreset(e.target.value as CanvasPreset)}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none focus-visible:ring-2 focus-visible:ring-teal-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                    >
                      {CANVAS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="setting-fit" className="text-sm font-bold text-slate-700 dark:text-slate-200">
                      Fit
                    </label>
                    <select
                      id="setting-fit"
                      value={fitMode}
                      onChange={(e) => setFitMode(e.target.value as CanvasFitMode)}
                      disabled={canvasPreset === 'original'}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none focus-visible:ring-2 focus-visible:ring-teal-500 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                    >
                      {FIT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <p className="text-[11px] text-slate-400 leading-tight">
                    Use blur to preserve the full video, crop to fill the frame, or bars for an untouched fit.
                  </p>
                </div>
              )}
            </div>

            <div className="bg-slate-50 dark:bg-slate-800 rounded-2xl p-4 border border-slate-100 dark:border-slate-700">
              <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
                <strong className="text-slate-700 dark:text-slate-300">Note:</strong> Browser conversion can be slow with long or high-resolution videos.
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SettingsPanel;
